import { useState, useEffect } from 'react';
import { collection, getDocs, query, where, or, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';

const useClassroomAuthors = (classrooms = []) => {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAuthors = async () => {
      if (!classrooms.length) {
        setAuthors([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Get unique author IDs from classrooms (check both userId and createdBy fields)
        const authorIds = [...new Set(classrooms.map(classroom => classroom.userId || classroom.createdBy).filter(Boolean))];
        
        if (authorIds.length === 0) {
          setAuthors([]);
          setLoading(false);
          return;
        }

        // Fetch each user document individually using their document ID
        const authorPromises = authorIds.map(async (authorId) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', authorId));
            if (userDoc.exists()) {
              return {
                id: userDoc.id,
                ...userDoc.data()
              };
            }
            return null;
          } catch (error) {
            console.error(`Error fetching user ${authorId}:`, error);
            return null;
          }
        });

        const authorsData = (await Promise.all(authorPromises)).filter(Boolean);
        
        setAuthors(authorsData);
        setError(null);
      } catch (err) {
        console.error('Error fetching classroom authors:', err);
        setError('Failed to fetch classroom authors');
      } finally {
        setLoading(false);
      }
    };

    fetchAuthors();
  }, [classrooms]);

  // Helper functions
  const getAuthor = (userId) => {
    return authors.find(author => author.id === userId);
  };

  const getAuthorName = (userId) => {
    const author = getAuthor(userId);
    return author ? author.name || author.email || 'Unknown Author' : 'Unknown Author';
  };

  // Get author name directly from classroom if author is an object
  const getAuthorNameFromClassroom = (classroom) => {
    if (classroom.author && typeof classroom.author === 'object') {
      return classroom.author.name || classroom.author.email || 'Unknown Author';
    }
    return getAuthorName(getAuthorId(classroom));
  };

  // Get author ID from classroom (check both userId and createdBy fields)
  const getAuthorId = (classroom) => {
    // Handle case where author might be an object with id field
    if (classroom.userId) return classroom.userId;
    if (classroom.author && typeof classroom.author === 'object' && classroom.author.id) {
      return classroom.author.id;
    }
    if (classroom.author && typeof classroom.author === 'string') {
      return classroom.author;
    }
    if (classroom.createdBy) return classroom.createdBy;
    return null;
  };

  const getAuthorStats = () => {
    const authorPostCounts = {};
    const authorMemberCounts = {};
    
    classrooms.forEach(classroom => {
      const authorId = getAuthorId(classroom);
      if (authorId) {
        authorPostCounts[authorId] = (authorPostCounts[authorId] || 0) + 1;
        
        // Calculate total members for this author
        const memberCount = classroom.memberCount || 0;
        authorMemberCounts[authorId] = (authorMemberCounts[authorId] || 0) + memberCount;
      }
    });

    return {
      authorPostCounts,
      authorMemberCounts,
      totalAuthors: Object.keys(authorPostCounts).length
    };
  };

  return {
    authors,
    loading,
    error,
    getAuthor,
    getAuthorName,
    getAuthorNameFromClassroom,
    getAuthorId,
    getAuthorStats
  };
};

export default useClassroomAuthors;
